/* eslint-disable react-refresh/only-export-components -- router module: lazy() chunks và helpers bọc Suspense */
import { lazy, Suspense } from 'react';

import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';

import { NotFoundPage } from '@/components/errors/NotFoundPage';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { OpsLayout } from '@/components/layouts/OpsLayout';
import { PageLoading } from '@/components/loading/PageLoading';
import { PATHS } from '@/config/paths';
import { MapAnalysisRegionDrawingProvider } from '@/features/map/context/MapAnalysisRegionDrawingContext';
import { OpsIndexRedirect } from '@/features/navigation/OpsIndexRedirect';
import { RequireAdminRoute } from '@/features/navigation/RequireAdminRoute';
import { RequireOpsRoute } from '@/features/navigation/RequireOpsRoute';

import { ProtectedRoute } from './routes/protected-route';
import { PublicRoute } from './routes/public-route';

const LoginPage = lazy(() =>
  import('@/features/auth/components/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/components/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);

const AuditLogPage = lazy(() =>
  import('@/features/analytics/components/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
);
const JobPipelinePage = lazy(() =>
  import('@/features/analytics/components/JobPipelinePage').then((m) => ({
    default: m.JobPipelinePage,
  })),
);
const NetcdfJobsPage = lazy(() =>
  import('@/features/analytics/components/NetcdfJobsPage').then((m) => ({
    default: m.NetcdfJobsPage,
  })),
);
const UserManagementPage = lazy(() =>
  import('@/features/analytics/components/UserManagementPage').then((m) => ({
    default: m.UserManagementPage,
  })),
);
const BasinManagementPage = lazy(() =>
  import('@/features/analytics/components/BasinManagementPage').then((m) => ({
    default: m.BasinManagementPage,
  })),
);
const StationManagementPage = lazy(() =>
  import('@/features/analytics/components/StationManagementPage').then((m) => ({
    default: m.StationManagementPage,
  })),
);
const SensorTypesManagementPage = lazy(() =>
  import('@/features/analytics/components/SensorTypesManagementPage').then((m) => ({
    default: m.SensorTypesManagementPage,
  })),
);
const RoleManagementPage = lazy(() =>
  import('@/features/analytics/components/RoleManagementPage').then((m) => ({
    default: m.RoleManagementPage,
  })),
);
const MenuManagementPage = lazy(() =>
  import('@/features/analytics/components/MenuManagementPage').then((m) => ({
    default: m.MenuManagementPage,
  })),
);
const FeaturePermissionMatrixPage = lazy(() =>
  import('@/features/menus/components/FeaturePermissionMatrixPage').then((m) => ({
    default: m.FeaturePermissionMatrixPage,
  })),
);
const CatalogManagementPage = lazy(() =>
  import('@/features/analytics/components/CatalogManagementPage').then((m) => ({
    default: m.CatalogManagementPage,
  })),
);
const ReportsOverviewPage = lazy(() =>
  import('@/features/reports/pages/ReportsOverviewPage').then((m) => ({
    default: m.ReportsOverviewPage,
  })),
);
const StationsReportPage = lazy(() =>
  import('@/features/reports/pages/StationsReportPage').then((m) => ({
    default: m.StationsReportPage,
  })),
);
const WaterLevelReportPage = lazy(() =>
  import('@/features/reports/pages/WaterLevelReportPage').then((m) => ({
    default: m.WaterLevelReportPage,
  })),
);
const RainfallReportPage = lazy(() =>
  import('@/features/reports/pages/RainfallReportPage').then((m) => ({
    default: m.RainfallReportPage,
  })),
);
const AlertsReportPage = lazy(() =>
  import('@/features/reports/pages/AlertsReportPage').then((m) => ({
    default: m.AlertsReportPage,
  })),
);
const FloodEventsReportPage = lazy(() =>
  import('@/features/reports/pages/FloodEventsReportPage').then((m) => ({
    default: m.FloodEventsReportPage,
  })),
);
const DamageReportPage = lazy(() =>
  import('@/features/reports/pages/DamageReportPage').then((m) => ({
    default: m.DamageReportPage,
  })),
);
const HistoricalComparisonReportPage = lazy(() =>
  import('@/features/reports/pages/HistoricalComparisonReportPage').then((m) => ({
    default: m.HistoricalComparisonReportPage,
  })),
);
const SystemConfigPage = lazy(() =>
  import('@/features/analytics/components/SystemConfigPage').then((m) => ({
    default: m.SystemConfigPage,
  })),
);
const AlertSettingsPage = lazy(() =>
  import('@/features/analytics/components/AlertSettingsPage').then((m) => ({
    default: m.AlertSettingsPage,
  })),
);
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

/** Bọc provider tại route để lazy map (2D/3D) luôn cùng cây React với context — tránh lỗi HMR/chunk. */
function DashboardWithMapDrawingProvider() {
  return (
    <MapAnalysisRegionDrawingProvider>
      <DashboardLayout />
    </MapAnalysisRegionDrawingProvider>
  );
}

/** BE/menu đôi khi trả `/admin/...` trong khi shell FE dùng `/ops/...`. */
function LegacyAdminToOpsRedirect() {
  const { pathname, search, hash } = useLocation();
  const target = pathname.replace(/^\/admin(?=\/|$)/, PATHS.OPS) + search + hash;
  return <Navigate to={target} replace />;
}

export const router = createBrowserRouter([
  {
    path: PATHS.LOGIN,
    element: (
      <PublicRoute>
        <AuthLayout />
      </PublicRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: PATHS.RESET_PASSWORD,
    element: (
      <PublicRoute>
        <AuthLayout />
      </PublicRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <ResetPasswordPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/admin/*',
    element: <LegacyAdminToOpsRedirect />,
  },
  {
    path: PATHS.HOME,
    element: (
      <ProtectedRoute>
        <DashboardWithMapDrawingProvider />
      </ProtectedRoute>
    ),
  },
  {
    path: PATHS.DASHBOARD,
    element: (
      <ProtectedRoute>
        <DashboardWithMapDrawingProvider />
      </ProtectedRoute>
    ),
  },
  {
    path: PATHS.MAP_IOT,
    element: (
      <ProtectedRoute>
        <DashboardWithMapDrawingProvider />
      </ProtectedRoute>
    ),
  },
  {
    path: '/data',
    element: (
      <ProtectedRoute>
        <OpsLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'upload',
        element: (
          <RequireOpsRoute navKey="netcdf">
            <SuspenseWrapper>
              <NetcdfJobsPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
    ],
  },
  {
    path: '/iot',
    element: (
      <ProtectedRoute>
        <OpsLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to={PATHS.IOT_STATIONS} replace /> },
      {
        path: 'stations',
        element: (
          <RequireOpsRoute navKey="stations">
            <SuspenseWrapper>
              <StationManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'sensor-types',
        element: (
          <RequireOpsRoute navKey="sensor-types">
            <SuspenseWrapper>
              <SensorTypesManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
    ],
  },
  {
    path: PATHS.OPS,
    element: (
      <ProtectedRoute>
        <OpsLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <OpsIndexRedirect /> },
      {
        path: 'health',
        element: <Navigate to={PATHS.OPS} replace />,
      },
      {
        path: 'audit',
        element: (
          <RequireOpsRoute navKey="audit">
            <SuspenseWrapper>
              <AuditLogPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      /** Alias thường gặp từ menu BE — redirect về `/ops/audit`. */
      {
        path: 'audit-log',
        element: (
          <RequireOpsRoute navKey="audit">
            <Navigate to={PATHS.OPS_AUDIT} replace />
          </RequireOpsRoute>
        ),
      },
      {
        path: 'jobs',
        element: (
          <RequireOpsRoute navKey="jobs">
            <SuspenseWrapper>
              <JobPipelinePage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'netcdf',
        element: <Navigate to={PATHS.DATA_UPLOAD} replace />,
      },
      {
        path: 'data/netcdf',
        element: <Navigate to={PATHS.DATA_UPLOAD} replace />,
      },
      {
        path: 'reports',
        children: [
          { index: true, element: <Navigate to={PATHS.OPS_REPORTS_OVERVIEW} replace /> },
          {
            path: 'overview',
            element: (
              <RequireOpsRoute navKey="reports">
                <SuspenseWrapper>
                  <ReportsOverviewPage />
                </SuspenseWrapper>
              </RequireOpsRoute>
            ),
          },
          {
            path: 'stations',
            element: (
              <RequireOpsRoute navKey="reports">
                <SuspenseWrapper>
                  <StationsReportPage />
                </SuspenseWrapper>
              </RequireOpsRoute>
            ),
          },
          {
            path: 'water-level',
            element: (
              <RequireOpsRoute navKey="reports">
                <SuspenseWrapper>
                  <WaterLevelReportPage />
                </SuspenseWrapper>
              </RequireOpsRoute>
            ),
          },
          {
            path: 'rainfall',
            element: (
              <RequireOpsRoute navKey="reports">
                <SuspenseWrapper>
                  <RainfallReportPage />
                </SuspenseWrapper>
              </RequireOpsRoute>
            ),
          },
          {
            path: 'alerts',
            element: (
              <RequireOpsRoute navKey="reports">
                <SuspenseWrapper>
                  <AlertsReportPage />
                </SuspenseWrapper>
              </RequireOpsRoute>
            ),
          },
          {
            path: 'flood-events',
            element: (
              <RequireOpsRoute navKey="reports">
                <SuspenseWrapper>
                  <FloodEventsReportPage />
                </SuspenseWrapper>
              </RequireOpsRoute>
            ),
          },
          {
            path: 'damage',
            element: (
              <RequireOpsRoute navKey="reports">
                <SuspenseWrapper>
                  <DamageReportPage />
                </SuspenseWrapper>
              </RequireOpsRoute>
            ),
          },
          {
            path: 'historical-comparison',
            element: (
              <RequireOpsRoute navKey="reports">
                <SuspenseWrapper>
                  <HistoricalComparisonReportPage />
                </SuspenseWrapper>
              </RequireOpsRoute>
            ),
          },
        ],
      },
      {
        path: 'users',
        element: (
          <RequireOpsRoute navKey="users">
            <SuspenseWrapper>
              <UserManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'basins',
        element: (
          <RequireOpsRoute navKey="basins">
            <SuspenseWrapper>
              <BasinManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'stations',
        element: (
          <RequireOpsRoute navKey="stations">
            <SuspenseWrapper>
              <StationManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'sensor-types',
        element: (
          <RequireOpsRoute navKey="sensor-types">
            <SuspenseWrapper>
              <SensorTypesManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'roles',
        element: (
          <RequireOpsRoute navKey="roles">
            <SuspenseWrapper>
              <RoleManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'permission-matrix',
        element: (
          <RequireOpsRoute navKey="permission-matrix">
            <RequireAdminRoute>
              <SuspenseWrapper>
                <FeaturePermissionMatrixPage />
              </SuspenseWrapper>
            </RequireAdminRoute>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'menus',
        element: (
          <RequireOpsRoute navKey="menus">
            <SuspenseWrapper>
              <MenuManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'catalog',
        element: (
          <RequireOpsRoute navKey="catalog">
            <SuspenseWrapper>
              <CatalogManagementPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'system-config',
        element: (
          <RequireOpsRoute navKey="system-config">
            <SuspenseWrapper>
              <SystemConfigPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
      {
        path: 'alert-settings',
        element: (
          <RequireOpsRoute navKey="alert-settings">
            <SuspenseWrapper>
              <AlertSettingsPage />
            </SuspenseWrapper>
          </RequireOpsRoute>
        ),
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
